import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
  // JSON-LD structured data
  structuredData?: object;
}

// Market-specific structured data props
interface MarketStructuredDataProps {
  title: string;
  description: string;
  category: string;
  url: string;
  image?: string;
  dateCreated?: string;
  dateModified?: string;
  status: string;
  options?: Array<{
    name: string;
    probability: number;
  }>;
}

const DEFAULT_TITLE = 'Wayja - Prediction Markets';
const DEFAULT_DESCRIPTION = 'Trade on real-world events. Create and trade on prediction markets for politics, sports, entertainment, and more.';
const DEFAULT_IMAGE = 'https://www.wayja.me/images/og-image.png';
const SITE_NAME = 'Wayja';
const BASE_URL = 'https://www.wayja.me';

// Generate JSON-LD for the website
export function getWebsiteStructuredData() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: BASE_URL,
    description: DEFAULT_DESCRIPTION,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/markets?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };
}

// Generate JSON-LD for organization
export function getOrganizationStructuredData() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: BASE_URL,
    logo: `${BASE_URL}/images/logo-linear.png`,
    image: `${BASE_URL}/images/logo-linear.png`,
    sameAs: [
      // Add social media links when available
    ]
  };
}

// Generate JSON-LD for a prediction market
export function getMarketStructuredData(market: MarketStructuredDataProps) {
  const structuredData: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: market.title,
    description: market.description,
    url: `${BASE_URL}${market.url}`,
    author: {
      '@type': 'Organization',
      name: SITE_NAME
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/images/logo-linear.png`,
        width: 466,
        height: 139
      }
    },
    articleSection: market.category,
    ...(market.image && { image: market.image }),
    ...(market.dateCreated && { datePublished: market.dateCreated }),
    ...(market.dateModified && { dateModified: market.dateModified }),
  };

  return structuredData;
}

// Generate JSON-LD for FAQ page
export function getFAQStructuredData(faqs: Array<{ question: string; answer: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
}

// Generate BreadcrumbList structured data
export function getBreadcrumbStructuredData(items: Array<{ name: string; url: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${BASE_URL}${item.url}`
    }))
  };
}

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  noindex = false,
  structuredData,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const fullUrl = url ? `${BASE_URL}${url}` : BASE_URL;

  // Default structured data includes website and organization
  const defaultStructuredData = [
    getWebsiteStructuredData(),
    getOrganizationStructuredData()
  ];

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
}
